import { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Lock, ArrowLeft } from "lucide-react";
import AlertModal from "../components/AlertModal";
import axios from "axios";

const ResetPassword = () => {
  const { token } = useParams();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: "", type: "info" });
  const navigate = useNavigate();

  const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  const isStrongPassword = (password) => {
    return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/.test(password);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isStrongPassword(password)) {
      setAlert({
        open: true,
        message: "Password must be at least 8 characters long and include uppercase, lowercase, number, and special character.",
        type: "error",
      });
      return;
    }

    if (password !== confirmPassword) {
      setAlert({
        open: true,
        message: "Passwords do not match.",
        type: "error",
      });
      return;
    }

    try {
      setLoading(true);

      await axios.put(
        `${BASE_URL}/api/auth/reset-password/${token}`,
        { password },
        { withCredentials: true }
      );

      setAlert({
        open: true,
        message: "Your password has been reset successfully!",
        type: "success",
      });

      setTimeout(() => {
        setAlert({ open: false, message: "", type: "info" });
        navigate("/reset-success");
      }, 2500);
    } catch (error) {
      setAlert({
        open: true,
        message:
          error.response?.data?.message ||
          "Failed to reset password. Please try again.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="h-[80vh] flex items-center justify-center px-4 bg-gray-100">
        <div className="w-full max-w-md bg-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
            Reset Password
          </h2>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <Lock className="w-4 h-4 text-gray-500" /> New Password
              </label>
              <input
                type="password"
                name="password"
                required
                placeholder="Enter new password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-4 py-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <Lock className="w-4 h-4 text-gray-500" /> Confirm Password
              </label>
              <input
                type="password"
                name="confirmPassword"
                required
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-4 py-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-md transition duration-200 ${
                loading ? "opacity-70 cursor-not-allowed" : ""
              }`}
            >
              {loading ? "Resetting..." : "Reset Password"}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/signin"
              className="inline-flex items-center text-sm text-blue-600 hover:underline"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to Sign In
            </Link>
          </div>
        </div>
      </div>

      <AlertModal
        isOpen={alert.open}
        onClose={() => setAlert({ open: false, message: "", type: "info" })}
        message={alert.message}
        type={alert.type}
      />
    </>
  );
};

export default ResetPassword;
